import { useEffect, useState, useCallback } from "react";
import qz from "qz-tray";
import { connectToQZTray } from "../Printers/ReceiptPrinters/printReceipt";
import axios from "axios";
import {
  getItemFromLocalStorage,
  removeItemFromStorage,
  setItemToLocalStorage,
} from "../../system";
import { storeConstants } from "../../constants";

const TERMINAL_PORT = "6868";
const ENDPOINT = "payrillium/local/test";

const usePayrilliumBaseUrl = (isPayrillium) => {
  const [baseUrl, setBaseUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scanForDevice = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await connectToQZTray();
      const device = await qz.networking.device();
      const networkPrefix = device.ip.split(".").slice(0, 3).join(".");
      const ipRange = Array.from(
        { length: 254 },
        (_, i) => `${networkPrefix}.${i + 1}`
      );

      const scanIP = async (ip) => {
        try {
          await axios.get(`http://${ip}:${TERMINAL_PORT}/${ENDPOINT}`, {
            timeout: 1000,
          });
          return ip;
        } catch {
          return null;
        }
      };

      const results = await Promise.allSettled(ipRange.map(scanIP));
      const foundIp = results
        .map((result) => result.value)
        .find((ip) => ip !== null && ip !== undefined);

      if (!foundIp) throw new Error("Payrillium device not found on network");

      const url = `http://${foundIp}:${TERMINAL_PORT}`;
      setItemToLocalStorage(
        storeConstants.PAYRILLIUM_BASE_URL_STORAGE_KEY,
        url
      );
      setBaseUrl(url);

      return url;
    } catch (err) {
      console.error("Scan error:", err);
      setError(err.message || "Unknown error");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPayrillium) {
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        const storedUrl = getItemFromLocalStorage(
          storeConstants.PAYRILLIUM_BASE_URL_STORAGE_KEY
        );
        if (storedUrl && storedUrl !== "null") {
          setBaseUrl(storedUrl);
          setLoading(false);
        } else {
          await scanForDevice();
        }
      } catch (err) {
        console.error("Initialization error:", err);
        setError(err.message || "Unknown error");
        setLoading(false);
      }
    };

    init();
  }, [scanForDevice, isPayrillium]);

  const refresh = useCallback(async () => {
    removeItemFromStorage(storeConstants.PAYRILLIUM_BASE_URL_STORAGE_KEY);
    setBaseUrl(null);
    return await scanForDevice();
  }, [scanForDevice]);

  return { baseUrl, loading, error, refresh };
};

export default usePayrilliumBaseUrl;
